/**
 * Query Key Factory
 *
 * Centralized management of all query keys for consistent cache invalidation.
 * Following TanStack Query best practices for hierarchical key structure.
 */

export const queryKeys = {
  /**
   * Posts and Feed
   */
  posts: {
    all: ["posts"] as const,
    lists: () => [...queryKeys.posts.all, "list"] as const,
    detail: (id: string) => [...queryKeys.posts.all, "detail", id] as const,
    engagement: (id: string) =>
      [...queryKeys.posts.detail(id), "engagement"] as const,
    comments: (postId: string) =>
      [...queryKeys.posts.detail(postId), "comments"] as const,
  },

  /**
   * Feed queries
   */
  feed: {
    all: ["feed"] as const,
    forYou: () => [...queryKeys.feed.all, "for-you"] as const,
    following: () => [...queryKeys.feed.all, "following"] as const,
    trending: () => [...queryKeys.feed.all, "trending"] as const,
    music: () => [...queryKeys.feed.all, "music"] as const,
    allPosts: () => [...queryKeys.feed.all, "all-posts"] as const,
    timeline: (userId: string) =>
      [...queryKeys.feed.all, "timeline", userId] as const,
  },

  /**
   * Users
   */
  users: {
    all: ["users"] as const,
    profile: (username: string) =>
      [...queryKeys.users.all, "profile", username] as const,
    followers: (userId: string) =>
      [...queryKeys.users.all, "followers", userId] as const,
    following: (userId: string) =>
      [...queryKeys.users.all, "following", userId] as const,
  },

  /**
   * Business directory queries
   */
  business: {
    all: ["business"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.business.all, "list", filters ?? {}] as const,
    featured: (limit: number) =>
      [...queryKeys.business.all, "featured", limit] as const,
    detail: (id: string) => [...queryKeys.business.all, "detail", id] as const,
    nearby: (lat: number, lng: number, radius?: number) =>
      [...queryKeys.business.all, "nearby", lat, lng, radius ?? null] as const,
    geocodeCity: (query: string) =>
      [...queryKeys.business.all, "geocode-city", query] as const,
    search: (query: string, filters?: Record<string, unknown>) =>
      [...queryKeys.business.all, "search", query, filters ?? {}] as const,
    categories: () => [...queryKeys.business.all, "categories"] as const,
    category: (slug: string) =>
      [...queryKeys.business.all, "category", slug] as const,
    // Prefix that matches every event query (list, by-business, detail) — use
    // for invalidation after event create/edit/delete so all of them refresh.
    eventsRoot: () => [...queryKeys.business.all, "events"] as const,
    events: (filters?: Record<string, unknown>) =>
      [...queryKeys.business.all, "events", filters ?? {}] as const,
    businessEvents: (businessId: string) =>
      [...queryKeys.business.all, "events", "business", businessId] as const,
    eventDetail: (ulid: string) =>
      [...queryKeys.business.all, "events", "detail", ulid] as const,
    eventFavorite: (ulid: string) =>
      [...queryKeys.business.all, "event-favorites", ulid, "check"] as const,
    myEventFavorites: () =>
      [...queryKeys.business.all, "event-favorites", "mine"] as const,
    myClaimed: () => [...queryKeys.business.all, "my-claimed"] as const,
    mine: () => [...queryKeys.business.all, "mine"] as const,
    analytics: (businessId: string) =>
      [...queryKeys.business.all, "analytics", businessId] as const,
    myAnalytics: () => [...queryKeys.business.all, "analytics", "mine"] as const,
    collections: (handle: string) =>
      [...queryKeys.business.all, "collections", handle] as const,
    checkSaved: (businessId: string) =>
      [...queryKeys.business.all, "check-saved", businessId] as const,
    cities: () => [...queryKeys.business.all, "cities"] as const,
    citySearch: (q: string) =>
      [...queryKeys.business.all, "cities", "search", q] as const,
    reviews: (businessUlid: string) =>
      [...queryKeys.business.all, "reviews", businessUlid] as const,
  },

  /**
   * Profile queries
   */
  profile: {
    all: ["profile"] as const,
    user: (username: string) => [...queryKeys.profile.all, username] as const,
    posts: (username: string, searchType?: string) =>
      [
        ...queryKeys.profile.user(username),
        "posts",
        searchType ?? "all",
      ] as const,
    songs: (username: string) =>
      [...queryKeys.profile.user(username), "songs"] as const,
    likes: (username: string) =>
      [...queryKeys.profile.user(username), "likes"] as const,
  },

  /**
   * Notifications
   */
  notifications: {
    all: ["notifications"] as const,
    list: (type?: string) =>
      [...queryKeys.notifications.all, "list", type] as const,
    unreadCount: () =>
      [...queryKeys.notifications.all, "unread-count"] as const,
  },

  /**
   * Search
   */
  search: {
    all: ["search"] as const,
    posts: (query: string) =>
      [...queryKeys.search.all, "posts", query] as const,
    users: (query: string) =>
      [...queryKeys.search.all, "users", query] as const,
  },

  /**
   * Trending
   */
  trending: {
    all: ["trending"] as const,
    posts: () => [...queryKeys.trending.all, "posts"] as const,
    hashtags: () => [...queryKeys.trending.all, "hashtags"] as const,
  },

  /**
   * Messages/Chat
   */
  messages: {
    all: ["messages"] as const,
    groups: () => [...queryKeys.messages.all, "groups"] as const,
    conversation: (groupUlid: string) =>
      [...queryKeys.messages.all, "conversation", groupUlid] as const,
  },

  /**
   * Playlists
   */
  playlists: {
    all: ["playlists"] as const,
    list: (username: string) =>
      [...queryKeys.playlists.all, "list", username] as const,
    detail: (playlistId: string) =>
      [...queryKeys.playlists.all, "detail", playlistId] as const,
    public: ["playlists", "public"] as const,
  },

  /**
   * CEO Dashboard
   */
  dashboard: {
    all: ["dashboard"] as const,
    summary: (params?: {
      preset?: string;
      startDate?: string;
      endDate?: string;
    }) => [...queryKeys.dashboard.all, "summary", params] as const,
    timeseries: (params: {
      metric: string;
      preset?: string;
      startDate?: string;
      endDate?: string;
    }) => [...queryKeys.dashboard.all, "timeseries", params] as const,
    listeningDistribution: (params?: {
      preset?: string;
      startDate?: string;
      endDate?: string;
    }) =>
      [...queryKeys.dashboard.all, "listening-distribution", params] as const,
    hourlyActiveUsers: () =>
      [...queryKeys.dashboard.all, "hourly-active-users"] as const,
  },
} as const;

/**
 * Helper function to invalidate all queries related to a post
 */
export function invalidatePostQueries(
  queryClient: {
    invalidateQueries: (opts: { queryKey: readonly string[] }) => void;
  },
  postId: string,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(postId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.posts.lists() });
  queryClient.invalidateQueries({ queryKey: queryKeys.feed.all });
}

/**
 * Helper function to invalidate all queries related to a user
 */
export function invalidateUserQueries(
  queryClient: {
    invalidateQueries: (opts: { queryKey: readonly string[] }) => void;
  },
  username: string,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.users.profile(username),
  });
  queryClient.invalidateQueries({ queryKey: queryKeys.profile.user(username) });
}
